import { ArrowLeft, ArrowRight, CalendarPlus, Check, CircleDot, Sparkles, X } from "lucide-react";
import { useEffect, useState } from "react";
import { chiNotify } from "@/components/ChiNotification";
import ResponsiveImage from "@/components/ResponsiveImage";
import EventPlatformField from "@/components/EventPlatformField";
import { trpc } from "@/lib/trpc";
import {
  composeEventPlatform,
  type EventPlatform,
  type EventPlatformParts,
} from "@shared/eventPlatform";
import ImageUploader from "@/components/ImageUploader";
import { eventFeaturedArtwork } from "@shared/eventPlaceholderArt";
import { CHI_EVENT_CATEGORIES, type CHIEventCategory } from "@shared/eventCategories";

const fieldStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 46,
  border: "1px solid rgba(255,255,255,.13)",
  borderRadius: ".72rem",
  background: "rgba(255,255,255,.055)",
  color: "white",
  padding: ".72rem .8rem",
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: "var(--chi-body-compact)",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 160ms cubic-bezier(.23,1,.32,1), box-shadow 160ms cubic-bezier(.23,1,.32,1), background 160ms cubic-bezier(.23,1,.32,1)",
};

/*
 * The label text and its asterisk on one line.
 *
 * labelStyle is a grid, and a bare text node beside <RequiredMarker /> makes two
 * anonymous grid items — so the asterisk was dropping to its own row underneath
 * the field name. Wrapping the pair in a single item keeps them together.
 */
const labelRowStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "baseline",
  gap: ".1rem",
};

const labelStyle: React.CSSProperties = {
  display: "grid",
  gap: ".42rem",
  color: "#C7D0DF",
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: "var(--chi-body-compact)",
  fontWeight: 700,
  letterSpacing: ".01em",
};

/*
 * One word per step, so the rail is a single line at any width. The labels are
 * narrow columns, and a two-word title broke across lines and pushed each step
 * taller than the one beside it.
 *
 * "Description" rather than "Story": the step collects the event description, and
 * naming it after the field it holds is one less thing to translate.
 */
/** Today in the input's own format, local rather than UTC so it does not jump a day. */
function todayInputValue() {
  const now = new Date();
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
  ].join("-");
}

/**
 * Timezones offered, with the abbreviation the published label uses.
 *
 * A short list rather than the full IANA set: these are the zones CHI runs
 * events in, and a 400-entry dropdown to pick Central is not a kindness.
 */
const EVENT_TIMEZONES = [
  { value: "America/Chicago", abbreviation: "CST", label: "Central (CST)" },
  { value: "America/New_York", abbreviation: "EST", label: "Eastern (EST)" },
  { value: "America/Denver", abbreviation: "MST", label: "Mountain (MST)" },
  { value: "America/Los_Angeles", abbreviation: "PST", label: "Pacific (PST)" },
  { value: "Europe/London", abbreviation: "GMT", label: "London (GMT)" },
] as const;

/** "19:00" to "7:00 PM". Empty in, empty out, so a half-filled range says nothing. */
function to12Hour(value: string) {
  if (!/^\d{2}:\d{2}$/.test(value)) return "";
  const [rawHours, minutes] = value.split(":");
  const hours = Number(rawHours);
  const suffix = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${minutes} ${suffix}`;
}

/**
 * The published time label, built from the range rather than typed.
 *
 * This used to be one free-text field, so "7pm-9pm", "7:00 PM – 9:00 PM CST" and
 * "19:00 CST" all existed side by side in the catalog and no two events read the
 * same way. The stored value keeps the same shape as before, so nothing
 * downstream has to change.
 */
function buildTimeLabel(start: string, end: string, timezoneAbbreviation: string) {
  const from = to12Hour(start);
  const to = to12Hour(end);
  if (!from) return "";
  return to ? `${from} – ${to} ${timezoneAbbreviation}` : `${from} ${timezoneAbbreviation}`;
}

const stepMeta = [
  { title: "Essentials", description: "Title, date & time" },
  { title: "Logistics", description: "Format, host & capacity" },
  { title: "Description", description: "Details & takeaways" },
  { title: "Publish", description: "Confirm & publish" },
] as const;

type EventStep = 1 | 2 | 3 | 4;

function RequiredMarker() {
  return <span aria-hidden="true" style={{ color: "#FF82C0", marginLeft: ".18rem" }}>*</span>;
}

/**
 * Slugify while someone is still typing.
 *
 * The previous version trimmed and stripped trailing hyphens on every keystroke,
 * so typing "leadership-" lost the hyphen before the next word could be typed and
 * a multi-word slug was impossible to enter by hand. A space or any other invalid
 * character becomes a hyphen, repeats collapse, and a single trailing hyphen is
 * allowed to stand — it is a word boundary in progress, not a mistake yet.
 */
function slugifyWhileTyping(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+/, "")
    .slice(0, 200);
}

/** The finished slug. Applied when the field is left, not as it is typed. */
function slugify(value: string) {
  return slugifyWhileTyping(value).replace(/-+$/, "");
}

function displayStepNumber(step: number) {
  return String(step).padStart(2, "0");
}

export default function CreateEventDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const utils = trpc.useUtils();
  const [activeStep, setActiveStep] = useState<EventStep>(1);
  const [stepError, setStepError] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [category, setCategory] = useState<CHIEventCategory>("Community");
  const [slug, setSlug] = useState<string>("");
  const [slugTouched, setSlugTouched] = useState<boolean>(false);
  /*
   * Checked a beat after typing stops rather than on every keystroke: the answer
   * only matters once the slug has settled, and a request per character would ask
   * the server about a dozen slugs nobody is going to use.
   */
  const [slugToCheck, setSlugToCheck] = useState<string>("");
  const [eventDate, setEventDate] = useState<string>("");
  const [startTime, setStartTime] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");
  const [timezone, setTimezone] = useState<string>(EVENT_TIMEZONES[0].value);
  /* Derived, never typed — see buildTimeLabel. */
  const timezoneMeta = EVENT_TIMEZONES.find(zone => zone.value === timezone) ?? EVENT_TIMEZONES[0];
  const timeLabel = buildTimeLabel(startTime, endTime, timezoneMeta.abbreviation);
  const [eventType, setEventType] = useState<"virtual" | "in_person">("virtual");
  const [location, setLocation] = useState<string>("");
  /*
   * The chosen platform is its own state, not derived from `location`.
   *
   * Deriving it looked tidier and was broken: "Other" contributes no prefix, so
   * choosing it before typing composes an empty string, which parses back as
   * "nothing selected" -- the detail field never appeared and the choice could
   * not be made. `location` is still the single submitted value; this only
   * remembers which option is showing.
   */
  const [locationPlatform, setLocationPlatformChoice] = useState<EventPlatform | "">("");
  /*
   * The detail is held too, for the same reason the platform is: reading it back
   * out of `location` means reading it through a parse that trims, so the trailing
   * space in "Zoom (link sent" disappeared between keystrokes and the words ran
   * together. `location` stays the one value that submits.
   */
  const [locationDetail, setLocationDetail] = useState<string>("");
  /*
   * The attendee link, held apart from `location` on purpose: `location` is public
   * catalog text, so a meeting link placed there is readable by anyone browsing the
   * events page. This posts to the event's `joinUrl`, which the schema validates as
   * HTTPS and which reaches attendees after they register.
   */
  const [joinUrl, setJoinUrl] = useState<string>("");
  const applyLocation = (parts: EventPlatformParts) => {
    setLocationPlatformChoice(parts.platform);
    setLocationDetail(parts.detail);
    setLocation(composeEventPlatform(parts));
  };
  const [speaker, setSpeaker] = useState<string>("");
  const [speakerOrg, setSpeakerOrg] = useState<string>("");
  const [seats, setSeats] = useState<string>("100");
  const [description, setDescription] = useState<string>("");
  const [highlights, setHighlights] = useState<string>("");
  const [eventPictureUrl, setEventPictureUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!slugTouched) setSlug(slugify(title));
  }, [title, slugTouched]);

  useEffect(() => {
    if (!open) {
      setActiveStep(1);
      setStepError("");
      setEventPictureUrl(null);
    }
  }, [open]);

  const createEvent = trpc.events.create.useMutation({
    onSuccess: async event => {
      await Promise.all([
        utils.events.publicCatalog.invalidate(),
        utils.events.managedEvents.invalidate(),
        utils.events.pricingPolicies.invalidate(),
      ]);
      const zoomProvisioning = event as typeof event & {
        zoomProvisioning?: "created" | "manual_action_required";
        zoomProvisioningMessage?: string;
      };
      if (zoomProvisioning.zoomProvisioning === "created") {
        chiNotify.success("Event and Zoom meeting created", {
          description: `${event.title} is now available. The protected Zoom link will appear in the event view and My Events only after an attendee successfully registers.`,
        });
      } else if (zoomProvisioning.zoomProvisioning === "manual_action_required") {
        chiNotify.info("Event created — Zoom needs attention", {
          description: zoomProvisioning.zoomProvisioningMessage ?? "Open the event in Events Management to create or attach its Zoom attendee link.",
        });
      } else {
        chiNotify.success("Event created", {
          description: `${event.title} is now available in the event catalog.`,
        });
      }
      onClose();
    },
    onError: error => {
      chiNotify.error("Event not created", { description: error.message });
    },
  });

  const uploadEventPicture = trpc.events.uploadArt.useMutation({
    onError: error => chiNotify.error("Event Picture not uploaded", { description: error.message }),
  });

  useEffect(() => {
    const settled = slugify(slug);
    if (!settled) {
      setSlugToCheck("");
      return;
    }
    const timer = window.setTimeout(() => setSlugToCheck(settled), 600);
    return () => window.clearTimeout(timer);
  }, [slug]);

  const slugCheck = trpc.events.slugAvailable.useQuery(
    { slug: slugToCheck },
    { enabled: open && slugToCheck.length > 0, retry: false, staleTime: 10_000 },
  );
  const slugTaken = slugCheck.data?.available === false;

  /*
   * Every hook above the guard, without exception.
   *
   * The dialog is mounted closed and opened by the Create Event action, so a
   * hook placed below this line runs on the open render but not the closed one
   * — and React tears the page down with "Rendered more hooks than during the
   * previous render" the moment the button is pressed.
   */
  if (!open) return null;

  const highlightsList = highlights
    .split("\n")
    .map(item => item.trim())
    .filter(Boolean);
  const eventPicture = eventFeaturedArtwork({
    slug: slug || "new-event",
    title: title || "New event",
    heroImage: eventPictureUrl,
  });

  function validateStep(step: EventStep) {
    if (step === 1) {
      if (title.trim().length < 3) return "Add an event title with at least three characters to continue.";
      if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) return "Use a lowercase URL slug with letters, numbers, and single hyphens.";
      /*
       * Blocked here rather than only at submit, so a duplicate is caught on the
       * step that owns the field instead of after three more steps of work. The
       * server enforces it as well — this is the courteous half of the check, not
       * the authoritative one.
       */
      if (slugTaken) return "That web address is already in use. Choose another before continuing.";
      if (slugCheck.isFetching || slugToCheck !== slugify(slug)) return "Still checking that the web address is free — one moment.";
      if (!eventDate) return "Choose the event date to continue.";
      if (!timeLabel.trim()) return "Add a clear time label to continue.";
    }

    if (step === 2) {
      if (!location.trim()) return "Add the event location or online venue to continue.";
      if (!speaker.trim()) return "Add the speaker, host, or event format to continue.";
      if (!speakerOrg.trim()) return "Add the host organization to continue.";
      const capacity = Number(seats);
      if (!Number.isInteger(capacity) || capacity < 1 || capacity > 100000) return "Set a whole-number seat capacity between 1 and 100,000.";
    }

    if (step === 3) {
      if (description.trim().length < 20) return "Add a description of at least 20 characters to continue.";
      if (!highlightsList.length) return "Add at least one attendee takeaway to continue.";
    }

    return "";
  }

  function goToNextStep() {
    const error = validateStep(activeStep);
    if (error) {
      setStepError(error);
      return;
    }
    setStepError("");
    setActiveStep(current => Math.min(current + 1, 4) as EventStep);
  }

  function goToPreviousStep() {
    setStepError("");
    setActiveStep(current => Math.max(current - 1, 1) as EventStep);
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (activeStep < 4) {
      goToNextStep();
      return;
    }

    const finalError = validateStep(1) || validateStep(2) || validateStep(3);
    if (finalError) {
      setStepError(finalError);
      setActiveStep(finalError.includes("title") || finalError.includes("slug") || finalError.includes("date") || finalError.includes("time") ? 1 : finalError.includes("location") || finalError.includes("speaker") || finalError.includes("organization") || finalError.includes("capacity") ? 2 : 3);
      return;
    }

    const date = new Date(`${eventDate}T12:00:00`);
    const displayDate = Number.isNaN(date.getTime())
      ? eventDate
      : date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    createEvent.mutate({
      slug,
      eventDate,
      displayDate,
      timeLabel: timeLabel.trim(),
      title: title.trim(),
      category,
      eventType,
      location: location.trim(),
      speaker: speaker.trim(),
      speakerOrg: speakerOrg.trim(),
      seats: Number(seats),
      description: description.trim(),
      highlights: highlightsList,
      timezone: "CT",
      heroImageUrl: eventPictureUrl ?? undefined,
      joinUrl: joinUrl.trim() || undefined,
    });
  }

  /*
   * Steps completed, not the step you are standing on: arriving at step 1 has
   * completed nothing, so it reads 0%. Reaching Review shows 75% because the
   * event is not created until it is submitted — a bar at 100% beside an unsaved
   * form claims work that has not happened.
   */
  const todayIso = todayInputValue();
  const progress = ((activeStep - 1) / stepMeta.length) * 100;
  const stepHeading = stepMeta[activeStep - 1];

  return (
    <div
      role="presentation"
      onMouseDown={event => event.target === event.currentTarget && onClose()}
      style={{ position: "fixed", inset: 0, zIndex: 3000, display: "grid", placeItems: "center", padding: "clamp(.6rem,2vw,1.25rem)", background: "rgba(3,6,17,.86)", backdropFilter: "blur(14px)" }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-event-title"
        aria-describedby="create-event-subtitle"
        onSubmit={submit}
        style={{ width: "min(900px,100%)", maxHeight: "min(860px,calc(100vh - 1.2rem))", overflowY: "auto", border: "1px solid rgba(233,30,140,.34)", borderRadius: "1.1rem", background: "linear-gradient(150deg,#12162b 0%,#0D1021 52%,#11162A 100%)", boxShadow: "0 34px 105px rgba(0,0,0,.62), 0 0 0 1px rgba(255,255,255,.025) inset", padding: "clamp(1rem,2.5vw,1.65rem)" }}
      >
        <header style={{ display: "flex", alignItems: "flex-start", gap: ".8rem", marginBottom: "1.15rem" }}>
          <span style={{ width: 42, height: 42, flexShrink: 0, display: "grid", placeItems: "center", borderRadius: ".85rem", background: "linear-gradient(135deg,rgba(233,30,140,.22),rgba(124,58,237,.18))", border: "1px solid rgba(255,130,192,.23)", color: "#FF5BAA" }}><CalendarPlus size={21} /></span>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ color: "#FF8CC5", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800 }}>Create a new event</h2>
          </div>
          <button type="button" aria-label="Close event creator" onClick={onClose} style={{ marginLeft: "auto", width: 36, height: 36, display: "grid", placeItems: "center", border: "1px solid rgba(255,255,255,.1)", borderRadius: ".65rem", background: "rgba(255,255,255,.025)", color: "#AEB9C9", cursor: "pointer" }}><X size={18} /></button>
        </header>

        <section aria-label="Event creation progress" style={{ padding: ".85rem", border: "1px solid rgba(255,255,255,.07)", borderRadius: ".95rem", background: "rgba(255,255,255,.025)", marginBottom: "1.15rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: ".8rem", alignItems: "center", marginBottom: ".7rem" }}>
            <p style={{ margin: 0, color: "#D5DDEC", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750 }}><span style={{ color: "#FF8CC5" }}>Step {activeStep} of {stepMeta.length}</span> · {stepHeading.title}</p>
            <p style={{ margin: 0, color: "#7F8BA0", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>{Math.round(progress)}% complete</p>
          </div>
          <div aria-hidden="true" style={{ height: 5, borderRadius: 99, overflow: "hidden", background: "rgba(255,255,255,.08)", marginBottom: ".85rem" }}><div style={{ width: "100%", height: "100%", borderRadius: "inherit", transformOrigin: "left", transform: `scaleX(${progress / 100})`, background: "linear-gradient(90deg,#4ADE80 0%,#10B981 60%,#39D7F4 100%)", transition: "transform 260ms cubic-bezier(.23,1,.32,1)" }} /></div>
          <ol className="create-event-stepper" style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: ".45rem" }}>
            {stepMeta.map((step, index) => {
              const stepNumber = index + 1;
              const isCurrent = stepNumber === activeStep;
              const isComplete = stepNumber < activeStep;
              return <li key={step.title}><button type="button" onClick={() => isComplete && setActiveStep(stepNumber as EventStep)} disabled={!isComplete && !isCurrent} aria-current={isCurrent ? "step" : undefined} style={{ width: "100%", minHeight: 57, padding: ".55rem", display: "flex", alignItems: "center", gap: ".45rem", textAlign: "left", border: `1px solid ${isComplete ? "rgba(74,222,128,.4)" : isCurrent ? "rgba(255,130,192,.42)" : "rgba(255,255,255,.06)"}`, borderRadius: ".68rem", /* Green once the step is behind you, pink while you are on it: done and
                 doing are different states and should not share a colour. */
              background: isComplete
                ? "linear-gradient(135deg,rgba(74,222,128,.18),rgba(16,185,129,.10))"
                : isCurrent
                  ? "linear-gradient(135deg,rgba(233,30,140,.14),rgba(124,58,237,.11))"
                  : "rgba(255,255,255,.018)", color: isComplete ? "#BBF7D0" : isCurrent ? "#FFF" : "#738198", cursor: isComplete ? "pointer" : "default" }}><span style={{ width: 23, height: 23, display: "grid", placeItems: "center", flexShrink: 0, borderRadius: "50%", background: isComplete ? "linear-gradient(135deg,#4ADE80,#10B981)" : isCurrent ? "#E91E8C" : "rgba(255,255,255,.06)", color: isComplete ? "#06281A" : isCurrent ? "#FFF" : "#738198", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 800 }}>{isComplete ? <Check size={13} strokeWidth={3} /> : displayStepNumber(stepNumber)}</span><span style={{ minWidth: 0 }}><span style={{ display: "block", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, lineHeight: 1.1, whiteSpace: "nowrap" }}>{step.title}</span></span></button></li>;
            })}
          </ol>
        </section>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".8rem", marginBottom: ".85rem" }}>
          <div><p style={{ margin: 0, color: "#FFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".95rem", fontWeight: 750 }}>{stepHeading.title}</p><p style={{ margin: ".18rem 0 0", color: "#7F8BA0", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>{stepHeading.description}</p></div>
          <p style={{ margin: 0, color: "#AEB9C9", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}><RequiredMarker /> Required</p>
        </div>

        {stepError && <div role="alert" style={{ display: "flex", alignItems: "flex-start", gap: ".5rem", marginBottom: ".85rem", padding: ".7rem .8rem", border: "1px solid rgba(251,113,133,.32)", borderRadius: ".7rem", background: "rgba(190,24,93,.12)", color: "#FFD1E2", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.45 }}><CircleDot size={15} style={{ marginTop: 1, flexShrink: 0 }} />{stepError}</div>}

        {/*
          alignItems:start on both steps, because these fields are different
          heights — the slug carries a URL preview and an availability line, the
          time block carries three controls — and a stretching grid made every
          short field as tall as the tallest one beside it, which is what made
          the form look uneven.
        */}
        {activeStep === 1 && <section aria-label="Event essentials" style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: ".85rem", alignItems: "start" }} className="event-pricing-grid">
          <label style={{ ...labelStyle, gridColumn: "1 / -1" }}><span style={labelRowStyle}>Event title<RequiredMarker /></span><input required aria-required="true" minLength={3} maxLength={220} value={title} onChange={event => setTitle(event.target.value)} onBlur={() => setTitle(value => value.trim())} placeholder="Enter the event name" style={fieldStyle} /></label>
          <label style={labelStyle}>
            <span style={labelRowStyle}>Category<RequiredMarker /></span>
            <select required aria-required="true" value={category} onChange={event => setCategory(event.target.value as CHIEventCategory)} style={{ ...fieldStyle, cursor: "pointer" }}>
              {CHI_EVENT_CATEGORIES.map(option => <option key={option} value={option}>{option}</option>)}
            </select>
          </label>
          <label style={{ ...labelStyle, gridColumn: "1 / -1" }}><span style={labelRowStyle}>Event URL slug<RequiredMarker /></span>
            <input
              required
              aria-required="true"
              aria-invalid={slugTaken || undefined}
              aria-describedby="event-slug-status"
              pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
              value={slug}
              onChange={event => { setSlugTouched(true); setSlug(slugifyWhileTyping(event.target.value)); }}
              /* Tidied when you leave the field, so a trailing hyphen typed on the
                 way to the next word is not corrected out from under you. */
              onBlur={() => setSlug(current => slugify(current))}
              placeholder="Enter the web address for this event"
              style={{ ...fieldStyle, borderColor: slugTaken ? "rgba(251,113,133,.7)" : undefined }}
            />
            {/* The address this becomes, so the slug is not an abstraction. */}
            <p style={{ margin: ".4rem 0 0", overflowWrap: "anywhere", color: "#718096", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
              https://thechialliance.com/events/<strong style={{ color: "#C7D0DF" }}>{slug || "{event-slug}"}</strong>
            </p>
            <p id="event-slug-status" role="status" style={{ margin: ".3rem 0 0", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", color: slugTaken ? "#FCA5A5" : slugCheck.data?.available ? "#BBF7D0" : "#718096" }}>
              {!slug
                ? "The address is built from the title; edit it if you want a different one."
                : slugCheck.isFetching
                  ? "Checking that address…"
                  : slugTaken
                    ? <>
                        That address is already used by {slugCheck.data?.conflict === "catalog" ? "a published catalog event" : "another event"}.
                        {slugCheck.data?.suggestion && (
                          <>
                            {" "}
                            <button
                              type="button"
                              onClick={() => { setSlugTouched(true); setSlug(slugCheck.data!.suggestion!); }}
                              style={{ padding: 0, border: 0, background: "none", color: "#7DEAFF", fontFamily: "inherit", fontSize: "inherit", textDecoration: "underline", cursor: "pointer" }}
                            >
                              Use {slugCheck.data.suggestion}
                            </button>
                          </>
                        )}
                      </>
                    : slugCheck.data?.available
                      ? "That address is available."
                      : ""}
            </p>
          </label>
          <label style={labelStyle}><span style={labelRowStyle}>Date<RequiredMarker /></span><input
            required
            aria-required="true"
            type="date"
            value={eventDate}
            min={todayIso}
            onChange={event => setEventDate(event.target.value)}
            /*
              Clicking anywhere in the field opens the calendar, not only the
              small icon at its right edge. showPicker is guarded because Firefox
              and older Safari do not implement it, where the icon still works.
            */
            onClick={event => {
              const input = event.currentTarget as HTMLInputElement & { showPicker?: () => void };
              try { input.showPicker?.(); } catch { /* not supported, or not user-activated */ }
            }}
            style={{ ...fieldStyle, cursor: "pointer" }}
          /></label>
          <div style={labelStyle}><span style={labelRowStyle}>
            Time<RequiredMarker /></span>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: ".45rem" }}>
              <input required aria-required="true" aria-label="Start time" type="time" value={startTime} onChange={event => setStartTime(event.target.value)} style={fieldStyle} />
              <span aria-hidden="true" style={{ color: "#718096", fontFamily: "'Inter', sans-serif" }}>to</span>
              <input aria-label="End time" type="time" value={endTime} min={startTime || undefined} onChange={event => setEndTime(event.target.value)} style={fieldStyle} />
            </div>
            <select aria-label="Timezone" value={timezone} onChange={event => setTimezone(event.target.value)} style={{ ...fieldStyle, marginTop: ".45rem", cursor: "pointer" }}>
              {EVENT_TIMEZONES.map(zone => <option key={zone.value} value={zone.value} style={{ background: "#0D1220" }}>{zone.label}</option>)}
            </select>
            {/* What gets published, so the shape is visible before saving. */}
            <p style={{ margin: ".4rem 0 0", color: "#718096", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
              {timeLabel ? <>Shows as <strong style={{ color: "#C7D0DF" }}>{timeLabel}</strong></> : "Pick a start time to see how it will read."}
            </p>
          </div>
        </section>}

        {activeStep === 2 && <section aria-label="Event logistics" style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: ".85rem", alignItems: "start" }} className="event-pricing-grid">
          <label style={labelStyle}>Event type<select value={eventType} onChange={event => setEventType(event.target.value as "virtual" | "in_person")} style={fieldStyle}><option value="virtual">Virtual</option><option value="in_person">In person</option></select></label>
          {/*
            A virtual event's location is a platform, so it is chosen rather than
            typed: the catalog had six spellings of the same Zoom line. The detail
            field appears once a platform is picked, and carries the joining note
            for a named platform or the whole location under "Other".

            An in-person event keeps a plain field, because none of the platform
            options describe a venue.
          */}
          {eventType === "virtual" ? (
            <EventPlatformField
              platform={locationPlatform}
              detail={locationDetail}
              joinUrl={joinUrl}
              onPlatformChange={(next, carriedDetail) => applyLocation({ platform: next, detail: carriedDetail })}
              onDetailChange={next => applyLocation({ platform: locationPlatform, detail: next })}
              onJoinUrlChange={setJoinUrl}
              fieldStyle={fieldStyle}
              labelStyle={labelStyle}
            />
          ) : (
            <label style={labelStyle}><span style={labelRowStyle}>Location<RequiredMarker /></span><input required aria-required="true" value={location} onChange={event => setLocation(event.target.value)} placeholder="Enter the city and state" style={fieldStyle} /></label>
          )}
          <label style={labelStyle}><span style={labelRowStyle}>Speaker or format<RequiredMarker /></span><input required aria-required="true" value={speaker} onChange={event => setSpeaker(event.target.value)} placeholder="Enter the speaker name or event format" style={fieldStyle} /></label>
          <label style={labelStyle}><span style={labelRowStyle}>Organization<RequiredMarker /></span><input required aria-required="true" value={speakerOrg} onChange={event => setSpeakerOrg(event.target.value)} placeholder="Enter the host organization" style={fieldStyle} /></label>
          <label style={{ ...labelStyle, maxWidth: 250 }}>Seat capacity<RequiredMarker /><input required aria-required="true" type="number" min={1} max={100000} value={seats} onChange={event => setSeats(event.target.value)} style={fieldStyle} /></label>
          <aside style={{ alignSelf: "end", padding: ".72rem .8rem", border: "1px solid rgba(57,215,244,.14)", borderRadius: ".72rem", background: "rgba(57,215,244,.055)", color: "#9CB5C5", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>Registration type and Stripe pricing are set in <strong style={{ color: "#C8F5FF" }}>Events Management</strong> after the event is created.</aside>
        </section>}

        {activeStep === 3 && <section aria-label="Event story and takeaways" style={{ display: "grid", gap: ".85rem" }}>
          <label style={labelStyle}><span style={labelRowStyle}>Description<RequiredMarker /></span><textarea required aria-required="true" minLength={20} maxLength={5000} rows={5} value={description} onChange={event => setDescription(event.target.value)} placeholder="Describe the event, who it is for, and why it matters" style={{ ...fieldStyle, minHeight: 148, resize: "vertical", lineHeight: 1.55 }} /></label>
          <label style={labelStyle}><span style={labelRowStyle}>Attendee takeaways<RequiredMarker /></span><span style={{ color: "#728096", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 400 }}>Add one distinct takeaway per line.</span><textarea required aria-required="true" rows={4} value={highlights} onChange={event => setHighlights(event.target.value)} style={{ ...fieldStyle, minHeight: 118, resize: "vertical", lineHeight: 1.55 }} placeholder={"A practical framework for better decisions\nNew relationships with peer leaders"} /></label>
          <div style={{ padding: ".95rem", border: "1px solid rgba(57,215,244,.16)", borderRadius: ".85rem", background: "linear-gradient(135deg,rgba(57,215,244,.055),rgba(233,30,140,.045))" }}>
            <ImageUploader
              label="Event Picture"
              description="Optional. Upload one landscape image for the event page and cards. If you skip it, CHI will show a branded gradient fallback."
              presetKeys={["facebook_wall"]}
              defaultPresetKey="facebook_wall"
              maxFiles={1}
              onUpload={async dataUrl => {
                const eventSlug = slugify(slug);
                if (!eventSlug || slugTaken) throw new Error("Choose an available event URL before uploading an Event Picture.");
                const stored = await uploadEventPicture.mutateAsync({
                  slug: eventSlug,
                  presetKey: "facebook_wall",
                  dataUrl,
                });
                setEventPictureUrl(stored.url);
                chiNotify.success("Event Picture ready", { description: "This image will be attached when you create the event." });
              }}
            />
            <div style={{ display: "flex", alignItems: "center", gap: ".7rem", marginTop: ".85rem" }}>
              <ResponsiveImage src={eventPicture.url} alt="" sizes="126px" style={{ width: 126, height: 66, borderRadius: ".5rem", objectFit: "cover", border: "1px solid rgba(255,255,255,.14)" }} />
              <div style={{ minWidth: 0 }}>
                <p style={{ margin: 0, color: eventPicture.isPlaceholder ? "#FCD34D" : "#86EFAC", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 750, fontSize: "var(--chi-body-compact)" }}>
                  {eventPicture.isPlaceholder ? "Gradient fallback selected" : "Event Picture selected"}
                </p>
                <p style={{ margin: ".18rem 0 0", color: "#7F8BA0", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.45 }}>
                  {eventPicture.isPlaceholder ? "You can add a real image now or leave the branded fallback in place." : "You can replace this image before creating the event."}
                </p>
              </div>
              {eventPictureUrl && <button type="button" onClick={() => setEventPictureUrl(null)} style={{ marginLeft: "auto", minHeight: 34, padding: ".42rem .62rem", border: "1px solid rgba(255,255,255,.13)", borderRadius: ".55rem", background: "rgba(255,255,255,.035)", color: "#C7D0DF", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "var(--chi-body-compact)", cursor: "pointer" }}>Use fallback</button>}
            </div>
          </div>
        </section>}

        {activeStep === 4 && <section aria-label="Review event details" style={{ display: "grid", gap: ".85rem" }}>
          <div style={{ padding: "1rem", border: "1px solid rgba(57,215,244,.18)", borderRadius: ".9rem", background: "linear-gradient(135deg,rgba(57,215,244,.09),rgba(233,30,140,.06))" }}>
            <div style={{ display: "flex", alignItems: "center", gap: ".45rem", color: "#8DF2FF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase" }}><Sparkles size={14} /> Ready to create</div>
            <h3 style={{ margin: ".52rem 0 .25rem", color: "#FFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "1.13rem", lineHeight: 1.25 }}>{title || "Untitled event"}</h3>
            <p style={{ margin: 0, color: "#AEB9C9", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>{eventDate || "Date pending"} · {timeLabel || "Time pending"} · {eventType === "virtual" ? "Virtual" : "In person"}</p>
            <div style={{ display: "flex", alignItems: "center", gap: ".65rem", marginTop: ".85rem" }}>
              <ResponsiveImage src={eventPicture.url} alt="" sizes="104px" style={{ width: 104, height: 54, borderRadius: ".45rem", objectFit: "cover", border: "1px solid rgba(255,255,255,.14)" }} />
              <span style={{ color: eventPicture.isPlaceholder ? "#FCD34D" : "#86EFAC", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "var(--chi-body-compact)" }}>{eventPicture.isPlaceholder ? "Branded gradient fallback" : "Event Picture attached"}</span>
            </div>
          </div>
          <div className="create-event-review-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: ".65rem" }}>
            {[{ label: "Category", value: category }, { label: "URL slug", value: slug || "—" }, { label: "Location", value: location || "—" }, { label: "Speaker or format", value: speaker || "—" }, { label: "Capacity", value: seats || "—" }].map(item => <div key={item.label} style={{ padding: ".72rem .78rem", border: "1px solid rgba(255,255,255,.07)", borderRadius: ".68rem", background: "rgba(255,255,255,.025)" }}><p style={{ margin: 0, color: "#718096", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, letterSpacing: ".08em", textTransform: "uppercase" }}>{item.label}</p><p style={{ margin: ".32rem 0 0", color: "#E4EAF4", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.4, overflowWrap: "anywhere" }}>{item.value}</p></div>)}
          </div>
          <div style={{ padding: ".85rem", border: "1px solid rgba(255,255,255,.07)", borderRadius: ".72rem", background: "rgba(255,255,255,.02)" }}><p style={{ margin: 0, color: "#98A6BB", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, letterSpacing: ".08em", textTransform: "uppercase" }}>Attendee takeaways</p><ul style={{ display: "grid", gap: ".34rem", margin: ".52rem 0 0", paddingLeft: "1.1rem", color: "#D5DDEC", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>{highlightsList.map(item => <li key={item}>{item}</li>)}</ul></div>
          <p style={{ margin: 0, color: "#7F8BA0", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>Creating this event publishes its content to the catalog. Registration mode and Stripe pricing remain editable in Events Management after creation.</p>
        </section>}

        <footer style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: ".7rem", marginTop: "1.25rem", paddingTop: "1rem", borderTop: "1px solid rgba(255,255,255,.07)" }}>
          <div style={{ display: "flex", gap: ".55rem" }}>
            <button type="button" onClick={onClose} style={{ minHeight: 42, padding: ".62rem .88rem", borderRadius: ".65rem", border: "1px solid rgba(255,255,255,.12)", background: "transparent", color: "#B5C0D1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: "pointer" }}>Cancel</button>
            {activeStep > 1 && <button type="button" onClick={goToPreviousStep} style={{ minHeight: 42, display: "inline-flex", alignItems: "center", gap: ".35rem", padding: ".62rem .88rem", borderRadius: ".65rem", border: "1px solid rgba(57,215,244,.25)", background: "rgba(57,215,244,.06)", color: "#9FF3FF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700, cursor: "pointer" }}><ArrowLeft size={15} /> Back</button>}
          </div>
          {activeStep < 4 ? <button type="button" onClick={goToNextStep} style={{ minHeight: 42, display: "inline-flex", alignItems: "center", gap: ".38rem", padding: ".62rem .98rem", border: 0, borderRadius: ".65rem", background: "linear-gradient(135deg,#E91E8C,#B91C72)", boxShadow: "0 10px 24px rgba(233,30,140,.22)", color: "white", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 800, cursor: "pointer" }}>Continue <ArrowRight size={15} /></button> : <button type="submit" disabled={createEvent.isPending} style={{ minHeight: 42, display: "inline-flex", alignItems: "center", gap: ".38rem", padding: ".62rem .98rem", border: 0, borderRadius: ".65rem", background: "linear-gradient(135deg,#E91E8C,#B91C72)", boxShadow: "0 10px 24px rgba(233,30,140,.22)", color: "white", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 800, cursor: createEvent.isPending ? "wait" : "pointer", opacity: createEvent.isPending ? .72 : 1 }}><Check size={15} />{createEvent.isPending ? "Creating…" : "Create event"}</button>}
        </footer>
      </form>
    </div>
  );
}
